import styled, { keyframes } from 'styled-components';

import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

type SpinnerProps = {
  className?: string;
};

export const Spinner = ({ className }: SpinnerProps) => (
  <StyledLoading data-slot="spinner" className={className}>
    <SpinnerRing />
  </StyledLoading>
);

const rotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const stretch = keyframes`
  0% {
    transform: rotate(0deg);
  }
  50% {
    transform: rotate(180deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const StyledLoading = styled.div`
  z-index: 1000;
  position: fixed;
  top: 50%;
  left: 50%;
  margin: -1.5rem 0 0 -1.5rem;
  width: 3rem;
  height: 3rem;
  display: block;
`;

const SpinnerRing = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  animation: ${rotate} 2s linear infinite;
  will-change: transform;

  /* Create the partial circle using pseudo-element */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: var(--spinner-border-width, 6px) solid transparent;
    border-top-color: ${theme.color.uiTrans50};
    border-right-color: ${theme.color.uiTrans50};
    box-sizing: border-box;
    animation: ${stretch} 1.5s ease-in-out infinite;
    will-change: transform;
  }
`;

export const RelativeSpinner = styled(Spinner)`
  && {
    position: relative;
    top: auto;
    left: auto;
    margin: auto;
  }
`;

export const SizedRelativeSpinner = styled(Spinner)<{
  size: number;
  $skipMargin?: boolean;
  $borderWidth?: number;
}>(({ size, $skipMargin, $borderWidth }) => {
  // When the spinner rotates, we need a margin at the bottom that is equal to the diagonal to the center of the square
  const marginBottom = Math.sqrt(Math.pow(size, 2) / 2) - size / 2;
  return {
    '&&': {
      position: 'relative',
      overflow: 'hidden',
      top: 'auto',
      left: 'auto',
      margin: $skipMargin ? '0' : `auto auto ${pxToRem(marginBottom)} auto`,
      width: pxToRem(size),
      height: pxToRem(size),
      display: 'block',
      '--spinner-border-width': $borderWidth ? pxToRem($borderWidth) : undefined,
    },
  };
});
